import type { send as sendReal } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import { integerToAmount } from '@actual-app/core/shared/util';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import type {
  CategoryEntity,
  CategoryGroupEntity,
} from '@actual-app/core/types/models';
/**
 * Escritura de la «Primera travesía» con las acciones reales de Actual
 * (`send`). Cada función es un acto: no toca React ni las prefs, devuelve
 * los ids creados y, cuando cambia una pref, su nuevo valor en bruto para
 * que quien llama la guarde (`useEscribirTravesia`).
 *
 * `send` se recibe como parámetro para poder probarlo con uno simulado.
 */
import type { TFunction } from 'i18next';

import { escribirTipo } from '#components/mobile/accounts/cuentas';
import {
  lineasDeObjetivo,
  notaConObjetivo,
} from '#components/mobile/budget/objetivos';
import type { Objetivo } from '#components/mobile/budget/objetivos';
import { separarEmoji, unirEmoji } from '#components/mobile/ui/emoji';

import {
  BASICAS,
  cofre,
  cofreMensual,
  cofreOcasional,
  cuotaMeta,
  datosMeta,
  fechaMeta,
  META_SALTAR,
  nombreCofre,
  nombreGrupo,
  nombreMeta,
} from './catalogo';
import type { GrupoTravesia, IdCofre, MetaElegida } from './catalogo';
import type { IdsTravesia } from './estado';
import { lineasRumbo } from './pasoRumbo';
import type { DatosRumbo } from './pasoRumbo';

export type Enviar = typeof sendReal;

/** Grupos de gasto con los que nace todo presupuesto (default-db). */
export const GRUPOS_FABRICA: readonly { id: string; name: string }[] = [
  { id: 'fc3825fd-b982-4b72-b768-5b30844cf832', name: 'Usual Expenses' },
  {
    id: 'a137772f-cf2f-4089-9432-822d2ddc1466',
    name: 'Investments and Savings',
  },
];

function esGrupoFabrica(g: CategoryGroupEntity): boolean {
  return (
    !g.is_income && GRUPOS_FABRICA.some(f => f.id === g.id || f.name === g.name)
  );
}

/** Pref `category-colors` (JSON `{ id: hueco }`) → objeto editable. */
function leerColores(raw: string | null | undefined): Record<string, number> {
  try {
    const v: unknown = raw ? JSON.parse(raw) : {};
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      return Object.fromEntries(
        Object.entries(v).filter(([, h]) => Number.isInteger(h)),
      );
    }
  } catch {
    // Pref estropeada: se empieza de cero.
  }
  return {};
}

function mismoNombre(a: string, b: string) {
  return a.trim().toUpperCase() === b.trim().toUpperCase();
}

// ---------------------------------------------------------------------------
// Limpieza de fábrica
// ---------------------------------------------------------------------------

/**
 * Borra los dos grupos de gasto de fábrica en inglés («Usual Expenses» e
 * «Investments and Savings», sin transferencia: no hay movimientos) y
 * traduce el grupo y la categoría «Income». «Starting Balances» no se
 * renombra (el servidor la busca por ese nombre). Se puede repetir: la
 * segunda vez no hace nada.
 *
 * @returns Los ids de los grupos borrados.
 */
export async function limpiarFabrica(
  send: Enviar,
  t: TFunction,
): Promise<string[]> {
  const { grouped } = await send('get-categories');
  const borrados: string[] = [];
  for (const g of grouped) {
    if (esGrupoFabrica(g)) {
      await send('category-group-delete', { id: g.id });
      borrados.push(g.id);
    }
  }
  const ingresos = t('Income');
  if (ingresos !== 'Income') {
    for (const g of grouped) {
      if (!g.is_income) {
        continue;
      }
      const { categories, ...grupo } = g;
      if (grupo.name === 'Income') {
        await send('category-group-update', { ...grupo, name: ingresos });
      }
      for (const c of categories ?? []) {
        if (c.name === 'Income') {
          await send('category-update', { ...c, name: ingresos });
        }
      }
    }
  }
  return borrados;
}

// ---------------------------------------------------------------------------
// Cuentas (paso 3)
// ---------------------------------------------------------------------------

export type CuentaTravesia = {
  nombre: string;
  /** Saldo actual en céntimos. */
  saldo: IntegerAmount;
};

/**
 * Crea las cuentas del paso 3 (en el presupuesto, tipo Corriente) con su
 * saldo inicial. Devuelve sus ids y la pref `cuentas-tipos` actualizada.
 */
export async function crearCuentas(
  send: Enviar,
  cuentas: readonly CuentaTravesia[],
  tiposRaw: string | null | undefined,
): Promise<{ ids: string[]; tiposRaw: string }> {
  const ids: string[] = [];
  let tipos = tiposRaw ?? '';
  for (const c of cuentas) {
    const id = await send('account-create', {
      name: c.nombre.trim(),
      balance: integerToAmount(c.saldo),
      offBudget: false,
    });
    ids.push(id);
    tipos = escribirTipo(tipos, id, 'corriente');
  }
  return { ids, tiposRaw: tipos };
}

// ---------------------------------------------------------------------------
// Categorías y objetivos (pasos 4–6)
// ---------------------------------------------------------------------------

export type OpcionesCrear = {
  /** Mes en curso `YYYY-MM` (fechas de los objetivos y `refresh-goals`). */
  mes: string;
  /** Día de hoy `YYYY-MM-DD`. */
  hoy: string;
  /** Pref `category-colors` actual. */
  coloresRaw: string | null | undefined;
  /** Lo ya creado (para retomar sin duplicar). */
  ids?: IdsTravesia;
};

export type ResultadoCrear = {
  grupos: Partial<Record<GrupoTravesia, string>>;
  /** Id del catálogo → id real. */
  categorias: Record<string, string>;
  /** Pref `category-colors` con el color de cada categoría nueva. */
  coloresRaw: string;
};

/** Objetivo de cada cofre: mensual con su sugerido; ocasional, anual. */
export function objetivoDeCofre(id: IdCofre, mes: string): Objetivo | null {
  const m = cofreMensual(id);
  if (m) {
    return { tipo: 'mensual', importe: m.sugerido, dia: null, modo: 'apartar' };
  }
  const o = cofreOcasional(id);
  if (o) {
    return {
      tipo: 'anual',
      importe: o.anual,
      fecha: `${monthUtils.addMonths(mes, 12)}-01`,
      cadaMeses: 12,
    };
  }
  return null;
}

/** Objetivo de la meta del paso 6. */
export function objetivoDeMeta(meta: MetaElegida, mes: string): Objetivo {
  return meta.tipo === 'mes'
    ? { tipo: 'mensual', importe: meta.importe, dia: null, modo: 'apartar' }
    : {
        tipo: 'una-vez',
        importe: meta.importe,
        fecha: fechaMeta(mes, meta.meses),
      };
}

async function asegurarGrupo(
  send: Enviar,
  grouped: CategoryGroupEntity[],
  nombre: string,
  idPrevio: string | undefined,
): Promise<{ id: string; categorias: CategoryEntity[] }> {
  const existente =
    grouped.find(g => idPrevio != null && g.id === idPrevio) ??
    grouped.find(g => !g.is_income && mismoNombre(g.name, nombre));
  if (existente) {
    return { id: existente.id, categorias: existente.categories ?? [] };
  }
  const id = await send('category-group-create', { name: nombre });
  return { id, categorias: [] };
}

/** Guarda las notas con objetivo y recalcula los objetivos del mes. */
async function guardarObjetivos(
  send: Enviar,
  objetivos: readonly { id: string; objetivo: Objetivo }[],
  mes: string,
  hoy: string,
) {
  if (objetivos.length === 0) {
    return;
  }
  for (const { id, objetivo } of objetivos) {
    await send('notes-save', {
      id,
      note: notaConObjetivo(null, lineasDeObjetivo(objetivo, hoy)),
    });
  }
  await send(
    'budget/store-note-templates',
    objetivos.map(o => o.id),
  );
  await send('budget/refresh-goals', { month: mes });
}

/**
 * Crea los grupos que hagan falta y los cofres elegidos («🛒 Supermercado»),
 * en el orden del catálogo, con su color y su objetivo (P1: los mensuales,
 * objetivo mensual con su sugerido; los ocasionales, anual). Los ya creados
 * (en `ids` o con el mismo nombre en su grupo) no se repiten.
 */
export async function crearCategorias(
  send: Enviar,
  t: TFunction,
  elegidos: readonly IdCofre[],
  opciones: OpcionesCrear,
): Promise<ResultadoCrear> {
  const { grouped } = await send('get-categories');
  const colores = leerColores(opciones.coloresRaw);
  const grupos: Partial<Record<GrupoTravesia, string>> = {};
  const categorias: Record<string, string> = {};
  const objetivos: { id: string; objetivo: Objetivo }[] = [];

  const porGrupo = new Map<'casa' | 'dia' | 'vez', IdCofre[]>();
  for (const id of elegidos) {
    const c = cofre(id);
    if (c) {
      porGrupo.set(c.grupo, [...(porGrupo.get(c.grupo) ?? []), id]);
    }
  }

  for (const [grupo, ids] of porGrupo) {
    const g = await asegurarGrupo(
      send,
      grouped,
      nombreGrupo(t, grupo),
      opciones.ids?.grupos?.[grupo],
    );
    grupos[grupo] = g.id;
    // `category-create` inserta al principio del grupo: se crean al revés
    // para que queden en el orden del catálogo.
    for (const id of [...ids].reverse()) {
      const c = cofre(id);
      if (!c) {
        continue;
      }
      const nombre = unirEmoji(c.emoji, nombreCofre(t, id));
      const previo = opciones.ids?.categorias?.[id];
      const existente =
        g.categorias.find(x => previo != null && x.id === previo) ??
        g.categorias.find(x => mismoNombre(x.name, nombre));
      if (existente) {
        categorias[id] = existente.id;
        continue;
      }
      const nueva = await send('category-create', {
        name: nombre,
        groupId: g.id,
        isIncome: false,
      });
      categorias[id] = nueva;
      colores[nueva] = c.k;
      const objetivo = objetivoDeCofre(id, opciones.mes);
      if (objetivo) {
        objetivos.push({ id: nueva, objetivo });
      }
    }
  }

  await guardarObjetivos(send, objetivos.reverse(), opciones.mes, opciones.hoy);
  return { grupos, categorias, coloresRaw: JSON.stringify(colores) };
}

/**
 * Crea el grupo Objetivos con la categoría de la meta («🛟 Fondo de
 * emergencia»), su color y su objetivo (una vez: importe y fecha; cada mes:
 * mensual). Si ya existe con ese nombre, la reutiliza y le pone el objetivo.
 */
export async function crearMeta(
  send: Enviar,
  t: TFunction,
  meta: MetaElegida,
  opciones: OpcionesCrear,
): Promise<{
  grupo: string;
  categoria: string;
  cuota: IntegerAmount;
  coloresRaw: string;
}> {
  const { grouped } = await send('get-categories');
  const colores = leerColores(opciones.coloresRaw);
  const datos = datosMeta(meta.id);
  const g = await asegurarGrupo(
    send,
    grouped,
    nombreGrupo(t, 'obj'),
    opciones.ids?.grupos?.obj,
  );
  const nombre = unirEmoji(datos.emoji, nombreMeta(t, meta));
  const existente = g.categorias.find(x => mismoNombre(x.name, nombre));
  const categoria =
    existente?.id ??
    (await send('category-create', {
      name: nombre,
      groupId: g.id,
      isIncome: false,
    }));
  if (!existente) {
    colores[categoria] = datos.k;
  }
  await guardarObjetivos(
    send,
    [{ id: categoria, objetivo: objetivoDeMeta(meta, opciones.mes) }],
    opciones.mes,
    opciones.hoy,
  );
  return {
    grupo: g.id,
    categoria,
    cuota: cuotaMeta(meta),
    coloresRaw: JSON.stringify(colores),
  };
}

// ---------------------------------------------------------------------------
// Paso 6: Rumbo también se planifica
// ---------------------------------------------------------------------------

/** Emoji y color de la categoría Rumbo (la brújula de la marca). */
export const EMOJI_RUMBO = '🧭';
const COLOR_RUMBO = 9;

/**
 * Crea la categoría Rumbo en el grupo Suscripciones (o los reutiliza: el id
 * guardado, o una categoría de gasto con ese nombre en cualquier grupo), con
 * su color y la nota de pago fijo mensual desde el fin de la prueba.
 */
export async function crearRumbo(
  send: Enviar,
  t: TFunction,
  nombre: string,
  datos: DatosRumbo,
  opciones: OpcionesCrear & { idPrevio?: string },
): Promise<{ id: string; coloresRaw: string }> {
  const { grouped } = await send('get-categories');
  const colores = leerColores(opciones.coloresRaw);
  const limpio = nombre.trim() || 'Rumbo';
  const gasto = grouped.filter(g => !g.is_income);
  const todas = gasto.flatMap(g => g.categories ?? []);
  const existente =
    todas.find(c => opciones.idPrevio != null && c.id === opciones.idPrevio) ??
    todas.find(c => mismoNombre(separarEmoji(c.name).resto, limpio));
  let id = existente?.id;
  if (!id) {
    const g = await asegurarGrupo(send, grouped, t('Subscriptions'), undefined);
    id = await send('category-create', {
      name: unirEmoji(EMOJI_RUMBO, limpio),
      groupId: g.id,
      isIncome: false,
    });
    colores[id] = COLOR_RUMBO;
  }
  const previa = await send('notes-get', { id });
  await send('notes-save', {
    id,
    note: notaConObjetivo(previa?.note, lineasRumbo(datos)),
  });
  await send('budget/store-note-templates', [id]);
  // También el mes del fin de la prueba: allí el teclado propone el precio.
  for (const month of new Set([opciones.mes, datos.mes])) {
    await send('budget/refresh-goals', { month });
  }
  return { id, coloresRaw: JSON.stringify(colores) };
}

/** Aparta el precio en el mes del fin de la prueba (lo asignado allí). */
export async function apartarRumbo(
  send: Enviar,
  id: string,
  datos: DatosRumbo,
) {
  await send('budget/budget-amount', {
    month: datos.mes,
    category: id,
    amount: datos.precio,
  });
}

// ---------------------------------------------------------------------------
// Reparto (paso 8) e Inicio (paso 9)
// ---------------------------------------------------------------------------

/** Escribe lo asignado del mes (solo los importes que cambian). */
export async function escribirReparto(
  send: Enviar,
  mes: string,
  amounts: readonly { category: string; amount: IntegerAmount }[],
) {
  if (amounts.length === 0) {
    return;
  }
  await send('budget/budget-amounts', { month: mes, amounts: [...amounts] });
}

/**
 * Pref `pinned-categories` con la meta y las dos primeras categorías
 * creadas (Fijadas de Inicio), sin duplicar las que ya estuvieran.
 */
export function fijarInicio(
  pinnedRaw: string | null | undefined,
  ids: IdsTravesia,
  orden: readonly string[],
): string {
  let previas: string[] = [];
  try {
    const v: unknown = pinnedRaw ? JSON.parse(pinnedRaw) : [];
    if (Array.isArray(v)) {
      previas = v.filter((x): x is string => typeof x === 'string');
    }
  } catch {
    previas = [];
  }
  const primeras = orden
    .map(id => ids.categorias?.[id])
    .filter((x): x is string => x != null)
    .slice(0, 2);
  const nuevas = [ids.meta?.id, ...primeras].filter(
    (x): x is string => x != null,
  );
  return JSON.stringify(
    [...nuevas, ...previas].filter((x, i, a) => a.indexOf(x) === i),
  );
}

// ---------------------------------------------------------------------------
// Saltar
// ---------------------------------------------------------------------------

export type ResultadoSaltar = {
  ids: IdsTravesia;
  coloresRaw: string;
};

/**
 * «Saltar»: limpieza de fábrica, los 6 cofres básicos y la meta fondo de
 * emergencia (1.000 € en 12 meses): 7 cofres. No crea cuentas. Sirve
 * también para aplicarla en silencio en pantalla ancha (P3).
 */
export async function saltar(
  send: Enviar,
  t: TFunction,
  opciones: OpcionesCrear,
): Promise<ResultadoSaltar> {
  await limpiarFabrica(send, t);
  const cats = await crearCategorias(send, t, BASICAS, opciones);
  const meta = await crearMeta(send, t, META_SALTAR, {
    ...opciones,
    coloresRaw: cats.coloresRaw,
    ids: {
      ...opciones.ids,
      grupos: { ...opciones.ids?.grupos, ...cats.grupos },
    },
  });
  return {
    ids: {
      ...opciones.ids,
      limpiado: true,
      grupos: { ...opciones.ids?.grupos, ...cats.grupos, obj: meta.grupo },
      categorias: { ...opciones.ids?.categorias, ...cats.categorias },
      meta: { id: meta.categoria, clave: META_SALTAR.id, cuota: meta.cuota },
    },
    coloresRaw: meta.coloresRaw,
  };
}
